"use client"
import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import { Container, NewText, useInViewConfig, useIsDesktop, useTransitionUpStyles } from "ui"
import { sharedColors } from "ui/theme/colors"
import { styleObjectToCss } from "ui/transitions/utils"
import { extractProperty, getUmbracoPDFUrl } from "utils"

const StyledContainer = styled.div<{ noBackground?: boolean; linkColor?: string }>`
  background: ${(p) => (p.noBackground ? "none" : "white")};
  padding-bottom: 24px;
  padding-top: 24px;
  max-width: 100%;
  a {
    color: ${(p) => (p.linkColor ? p.linkColor : "inherit")};
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 1040px;
    margin: 40px auto;
    padding: 0;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 1280px;
  }

  p {
    margin: 0px !important;
  }
`

const StyledWrapper = styled.div<{ notCentered?: boolean }>`
  border-radius: 8px;
  > div > div {
    background: transparent;
    display: inline-block;
  }
  div,
  h1,
  p {
    margin-left: auto;
    margin-right: auto;
    color: inherit;
    text-align: center;
  }
  p {
    max-width: 675px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 100vw;
    margin-left: 0;
    margin-right: 0;
    background-position-y: center;
    border-radius: unset;
    h1 {
      font-size: 48px;
    }
    div,
    p {
      font-size: 18px;
    }
  }
  ${(props) =>
    props.notCentered &&
    css`
      div,
      h1,
      p {
        margin-left: auto;
        margin-right: auto;
        text-align: start;
      }
    `}
`
type Props = {
  data: any
  noTransition?: boolean
  hasBanner: boolean
  notCentered?: boolean
  noBackground?: boolean
  linkColor?: string
}

export const MapContentText: React.FC<Props> = ({
  data,
  noTransition,
  notCentered,
  noBackground,
  linkColor,
}) => {
  const isDesktop = useIsDesktop()
  const title = extractProperty(data, "conetntTitle")
  const subTitle = extractProperty(data, "subTitle")
  const innerText = extractProperty(data, "innerText")
  const { ref, inView } = useInView({
    ...useInViewConfig,
    threshold: isDesktop ? useInViewConfig.threshold : 0.1,
  })
  const titleTransitionStyles = useTransitionUpStyles(noTransition || inView)
  const subtitleTransitionStyles = useTransitionUpStyles(noTransition || inView)
  const descriptionTransitionStyles = useTransitionUpStyles(noTransition || inView)
  function cleanEmptyHtmlElements(html: string) {
    if (!html) {
      return html
    }
    // This regex finds empty HTML tags - an opening tag followed by a closing tag with no content in between
    // Note: This is a simplistic approach and may not handle nested structures or scripts/styles content properly
    const regex = /<(\w+)([^>]*)>\s*<\/\1>/g

    // Replace all instances of empty HTML elements with an empty string
    let cleanedHtml = html
    let previousHtml
    do {
      previousHtml = cleanedHtml
      cleanedHtml = cleanedHtml.replace(regex, "")
    } while (cleanedHtml !== previousHtml) // Repeat until no more replacements are made

    return cleanedHtml
  }
  const replacePDFUrlWithUmbracoUrl = () => {
    const regex = /(\/[^"]+\.(pdf|docx|doc|xls|xlsx))/g
    const newText = innerText.replace(regex, (match: any) => {
      return getUmbracoPDFUrl(match)
    })
    // eslint-disable-next-line no-useless-escape
    return cleanEmptyHtmlElements(newText)?.replace(/\/(he-il|en-us)\/[^\/]+\/pages/g, "")
  }

  return (
    <StyledWrapper notCentered={notCentered}>
      <StyledContainer ref={ref} noBackground={noBackground} linkColor={linkColor}>
        <Container>
          <NewText
            desktopFontSize={40}
            desktopLineHeight="44px"
            desktopFontWeight={400}
            mobileFontSize={32}
            mobileLineHeight={"110%"}
            component="h2"
            color={sharedColors.black}
            cssString={styleObjectToCss(titleTransitionStyles)}
          >
            {title}
          </NewText>
          {subTitle && (
            <NewText
              desktopFontSize={18}
              desktopLineHeight="26px"
              component="h3"
              desktopFontWeight="600"
              mobileFontSize={16}
              color={sharedColors.black}
              cssString={styleObjectToCss(subtitleTransitionStyles)}
            >
              {subTitle}
            </NewText>
          )}
          <NewText
            desktopFontSize={20}
            desktopLineHeight="100%"
            mobileFontSize={18}
            mobileLineHeight="100%"
            component="div"
            color={sharedColors.black}
            dangerouslySetInnerHTML={{
              __html: replacePDFUrlWithUmbracoUrl(),
            }}
            cssString={styleObjectToCss(descriptionTransitionStyles)}
            marginTop={"8px"}
            minHeight={"29px"}
          />
        </Container>
      </StyledContainer>
    </StyledWrapper>
  )
}
export default MapContentText
